import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar, Plus, Trash2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClosedDaysManager({ getHeaders }) {
  const [closed, setClosed] = useState([]);
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('Travaux');
  const [loading, setLoading] = useState(false);

  const fetchClosed = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/closed-days`);
      setClosed(res.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchClosed(); }, [fetchClosed]);

  const addClosed = async (e) => {
    e?.preventDefault?.();
    if (!date) { toast.error('Choisissez une date'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/closed-days`, { date, reason: reason.trim() || 'Ferme' }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true,
      });
      toast.success(`Le ${date} a ete marque comme ferme`);
      setDate(''); setReason('Travaux');
      fetchClosed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally { setLoading(false); }
  };

  const removeClosed = async (closedId, day) => {
    if (!window.confirm(`Reouvrir la location pour le ${day} ?`)) return;
    try {
      await axios.delete(`${API}/admin/closed-days/${closedId}`, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success(`Le ${day} est a nouveau reservable`);
      fetchClosed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = closed.filter(c => c.date >= today);
  const past = closed.filter(c => c.date < today);

  return (
    <div className="space-y-5" data-testid="closed-days-manager">
      <div className="bg-white border border-[#E2E2DF] rounded-xl p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[#C77D63]/10 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-5 h-5 text-[#C77D63]" />
          </div>
          <div>
            <h3 className="font-heading text-base font-bold text-[#1A3C34]">Marquer un jour comme ferme</h3>
            <p className="text-xs text-[#737373] mt-0.5">
              Les utilisateurs verront un bandeau d'indisponibilite sur le calendrier et ne pourront pas reserver ce jour-la (ni dans une location recurrente).
            </p>
          </div>
        </div>
        <form onSubmit={addClosed} className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-3 items-end">
          <div>
            <label className="text-xs font-semibold text-[#1A3C34]">Date</label>
            <Input
              type="date"
              min={today}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-10 mt-1 border-[#E2E2DF]"
              data-testid="closed-day-date-input"
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#1A3C34]">Motif (visible par les visiteurs)</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Travaux, conge, evenement prive..."
              className="h-10 mt-1 border-[#E2E2DF]"
              data-testid="closed-day-reason-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="h-10 bg-[#C77D63] text-white hover:bg-[#b06a51] font-bold"
            data-testid="closed-day-submit"
          >
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </form>
      </div>

      {/* Upcoming */}
      <div className="bg-white border border-[#E2E2DF] rounded-xl p-5">
        <h3 className="font-heading text-base font-bold text-[#1A3C34] mb-3">
          Fermetures a venir ({upcoming.length})
        </h3>
        {upcoming.length === 0 ? (
          <p className="text-sm text-[#737373] flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> Aucune fermeture programmee.
          </p>
        ) : (
          <div className="divide-y divide-[#E2E2DF]">
            {upcoming.map((c) => (
              <div key={c.closed_id} className="flex items-center justify-between py-2.5" data-testid={`closed-day-${c.date}`}>
                <div>
                  <p className="font-mono text-sm font-semibold text-[#1A3C34]">{formatFR(c.date)}</p>
                  <p className="text-xs text-[#737373]">{c.reason}</p>
                </div>
                <button
                  onClick={() => removeClosed(c.closed_id, c.date)}
                  className="text-red-500 hover:text-red-600 p-2 rounded-md hover:bg-red-50 transition-colors"
                  title="Reouvrir ce jour"
                  data-testid={`closed-day-delete-${c.date}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past (collapsed view) */}
      {past.length > 0 && (
        <details className="bg-white border border-[#E2E2DF] rounded-xl p-5">
          <summary className="font-heading text-sm font-bold text-[#737373] cursor-pointer">
            Historique ({past.length})
          </summary>
          <div className="divide-y divide-[#E2E2DF] mt-3">
            {past.slice(-50).reverse().map((c) => (
              <div key={c.closed_id} className="flex items-center justify-between py-2 opacity-70">
                <div>
                  <p className="font-mono text-xs text-[#737373]">{formatFR(c.date)}</p>
                  <p className="text-xs text-[#737373]">{c.reason}</p>
                </div>
                <button
                  onClick={() => removeClosed(c.closed_id, c.date)}
                  className="text-red-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50"
                  title="Supprimer de l'historique"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function formatFR(iso) {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return iso; }
}
